import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { BookMarked, AlertCircle } from "lucide-react";

/**
 * Renders the raw source text an admin saved for a chapter as a formatted
 * textbook page — detects headings (ALL CAPS or "Chapter N:" style), bullet
 * lines (•, -, *, ▪), numbered lists (1., 1)), sub-headings ending with ':'
 * and lays them out with proper typography while preserving the exact
 * wording (copyright is owned by the institution).
 */
export default function SourceView({ chapterId }) {
  const { isRTL } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr("");
    api.get(`/chapters/${chapterId}/source`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load the source.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chapterId]);

  const blocks = useMemo(() => parseBlocks(data?.text || ""), [data?.text]);

  if (loading) return (
    <div className="bg-white border border-border rounded-2xl p-8 text-center text-muted-foreground">
      {isRTL ? "جاري التحميل…" : "Loading…"}
    </div>
  );

  if (err) return (
    <div className="bg-white border border-terracotta/30 rounded-2xl p-6 text-terracotta flex items-start gap-3">
      <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="text-sm">{err}</div>
    </div>
  );

  if (blocks.length === 0) return (
    <div className="bg-white border border-dashed border-border rounded-2xl p-10 text-center" data-testid="source-empty">
      <div className="mx-auto w-12 h-12 rounded-xl bg-sage/10 text-sage flex items-center justify-center mb-3">
        <BookMarked className="w-6 h-6" strokeWidth={1.7} />
      </div>
      <h3 className="font-display text-lg font-semibold mb-1">
        {isRTL ? "لم يُضَف نص للفصل بعد" : "No source text yet"}
      </h3>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">
        {isRTL
          ? "سيقوم المسؤول بإضافة نص المرجع الأصلي قريباً."
          : "Your admin will add the textbook material shortly."}
      </p>
    </div>
  );

  return (
    <article
      className="bg-white border border-border rounded-2xl p-6 sm:p-12 max-w-none"
      data-testid="source-content"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="flex items-center gap-2 mb-6 text-[10px] uppercase tracking-[0.2em] text-muted-foreground pb-4 border-b border-border">
        <BookMarked className="w-3.5 h-3.5 text-sage" />
        {isRTL ? "النص المرجعي" : "Textbook source"}
        {data?.chapter_number && (
          <span className="text-sage font-semibold">
            · {isRTL ? "الفصل" : "Chapter"} {String(data.chapter_number).padStart(2, "0")}
          </span>
        )}
        {data?.updated_at && (
          <span className="ms-auto normal-case tracking-normal text-[11px]">
            {isRTL ? "آخر تحديث:" : "Updated"} {new Date(data.updated_at).toLocaleDateString()}
          </span>
        )}
      </div>
      <div className="space-y-5 text-[15.5px] leading-[1.85] text-foreground selection:bg-sage/20">
        {blocks.map((b, i) => <Block key={`blk-${i}`} block={b} />)}
      </div>
    </article>
  );
}

// ---------- block renderer ----------
function Block({ block }) {
  if (block.type === "h1") {
    return (
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 pt-4 border-t border-sage/30 first:border-t-0 first:pt-0">
        {block.text}
      </h1>
    );
  }
  if (block.type === "h2") {
    return (
      <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-8 text-sage border-s-4 border-sage ps-4 py-1">
        {block.text}
      </h2>
    );
  }
  if (block.type === "h3") {
    return (
      <h3 className="font-display text-xl font-semibold tracking-tight mt-6 text-terracotta">
        {block.text}
      </h3>
    );
  }
  if (block.type === "ul") {
    return (
      <ul className="list-none space-y-2 ps-0">
        {block.items.map((it, i) => (
          <li key={`li-${i}`} className="flex items-start gap-3 bg-secondary/40 rounded-lg px-4 py-2">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-sage shrink-0" />
            <span className="flex-1">{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "ol") {
    return (
      <ol className="space-y-2 ps-0">
        {block.items.map((it, i) => (
          <li key={`oli-${i}`} className="flex items-start gap-3 bg-white border border-border rounded-lg px-4 py-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-sage text-white text-[11px] font-bold shrink-0 mt-0.5">
              {i + 1}
            </span>
            <span className="flex-1">{it}</span>
          </li>
        ))}
      </ol>
    );
  }
  // paragraph (default)
  return <p className="text-foreground/90">{block.text}</p>;
}

// ---------- simple parser ----------
/**
 * Converts raw textbook text into structured blocks without altering the
 * wording. Heuristics:
 *   - A line that's ALL CAPS and short (< 60 chars) → h1
 *   - A line matching /^Chapter \d/i → h1
 *   - A line ending with ':' and < 80 chars → h3
 *   - A line that is Title Case and short (no terminal punctuation) → h2
 *   - Consecutive lines starting with • - * ▪ → ul
 *   - Consecutive lines starting with N. N) → ol (text after the marker kept)
 *   - Anything else → paragraph (lines joined with a space until blank line)
 */
function parseBlocks(raw) {
  const text = (raw || "").replace(/\r\n?/g, "\n").trim();
  if (!text) return [];

  const lines = text.split("\n");
  const blocks = [];
  let pBuf = [];
  let ulBuf = [];
  let olBuf = [];

  const flushP = () => {
    if (pBuf.length > 0) {
      blocks.push({ type: "p", text: pBuf.join(" ").trim() });
      pBuf = [];
    }
  };
  const flushUl = () => {
    if (ulBuf.length > 0) { blocks.push({ type: "ul", items: ulBuf }); ulBuf = []; }
  };
  const flushOl = () => {
    if (olBuf.length > 0) { blocks.push({ type: "ol", items: olBuf }); olBuf = []; }
  };
  const flushAll = () => { flushP(); flushUl(); flushOl(); };

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (!line) { flushAll(); continue; }

    // heading detectors
    if (/^chapter\s+\d/i.test(line)) {
      flushAll(); blocks.push({ type: "h1", text: line }); continue;
    }
    if (isAllCapsHeading(line)) {
      flushAll(); blocks.push({ type: "h1", text: toTitle(line) }); continue;
    }
    if (isTitleCaseHeading(line)) {
      flushAll(); blocks.push({ type: "h2", text: line }); continue;
    }
    if (isSubHeading(line)) {
      flushAll(); blocks.push({ type: "h3", text: line.replace(/:$/, "") }); continue;
    }

    // bullets
    const bulletMatch = line.match(/^([•·\-*▪▫◦])\s+(.*)$/);
    if (bulletMatch) {
      flushP(); flushOl(); ulBuf.push(bulletMatch[2]); continue;
    }
    // ordered list: 1. / 1) / (1)
    const olMatch = line.match(/^\(?(\d{1,2})[.)]\s+(.*)$/);
    if (olMatch) {
      flushP(); flushUl(); olBuf.push(olMatch[2]); continue;
    }

    // paragraph continuation
    flushUl(); flushOl();
    pBuf.push(line);
  }
  flushAll();
  return blocks;
}

function isAllCapsHeading(line) {
  if (line.length > 80 || line.length < 3) return false;
  const letters = line.replace(/[^A-Za-z]/g, "");
  if (letters.length < 3) return false;
  if (letters !== letters.toUpperCase()) return false;
  // Avoid misclassifying short acronym-heavy sentences with lots of digits/punct.
  const lettersRatio = letters.length / line.length;
  return lettersRatio > 0.5 && !line.endsWith(".");
}

function isTitleCaseHeading(line) {
  if (line.length > 70 || line.length < 4) return false;
  if (/[.!?]$/.test(line)) return false;
  if (/^[a-z]/.test(line)) return false;
  const words = line.split(/\s+/);
  if (words.length < 2 || words.length > 10) return false;
  const cap = words.filter((w) => /^[A-Z]/.test(w)).length;
  return cap / words.length >= 0.6;
}

function isSubHeading(line) {
  if (!line.endsWith(":") || line.length > 90) return false;
  if (/[.!?]/.test(line.slice(0, -1))) return false;
  return true;
}

function toTitle(s) {
  return s.toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase());
}
